import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import BrandHeader from "@/components/BrandHeader";
import AnalysisView from "@/components/AnalysisView";
import { api, API, getToken } from "@/lib/api";

export default function AdminDetail() {
  const { submissionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/submissions/${submissionId}`);
        setData(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [submissionId]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/submissions/${submissionId}/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TravelTrucks_${(data?.full_name || "candidate").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="min-h-screen">
      <BrandHeader minimal />
      <main className="max-w-5xl mx-auto px-6 sm:px-8 py-12">
        <Link
          to="/admin"
          data-testid="back-to-admin"
          className="text-xs uppercase tracking-[0.2em] text-[var(--tt-muted)] hover:text-[var(--tt-primary)]"
        >
          ← All Submissions
        </Link>

        {data && (
          <div className="float-right">
            <button
              onClick={downloadPdf}
              data-testid="download-pdf-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-hover)] text-white text-sm font-medium tracking-wide transition-colors"
            >
              ↓ Download Branded PDF
            </button>
          </div>
        )}

        {loading && (
          <div className="mt-10 p-10 rounded-2xl bg-[var(--tt-surface)] border border-[var(--tt-border)] text-[var(--tt-muted)]" data-testid="detail-loading">
            Loading…
          </div>
        )}

        {data && (
          <>
            <div className="mt-6">
              <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-primary)] font-semibold mb-3">
                Candidate Profile
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter" data-testid="candidate-name">
                {data.full_name}
              </h1>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-[var(--tt-muted)]">
                <span>{data.email}</span>
                <span>· {data.role_applied}</span>
                {data.location && <span>· {data.location}</span>}
                <span>· Submitted {new Date(data.created_at).toLocaleString()}</span>
              </div>
            </div>

            <AnalysisView analysis={data.ai_analysis} />

            {/* Raw responses */}
            <div className="mt-12 rounded-2xl bg-[var(--tt-surface)] border border-[var(--tt-border)] p-8" data-testid="raw-responses">
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--tt-primary)] font-semibold mb-6">
                Their Own Words
              </div>
              <div className="space-y-8">
                {data.answers.map((a, i) => (
                  <div key={i} className="border-l-2 border-[var(--tt-primary)] pl-5">
                    <div className="text-xs uppercase tracking-[0.18em] text-[var(--tt-muted)]">
                      {a.part} · {a.category}
                    </div>
                    <p className="font-display text-lg font-medium leading-snug mt-1">{a.question}</p>
                    <p className="mt-3 text-[var(--tt-text)] whitespace-pre-wrap leading-relaxed">
                      {a.response}
                    </p>
                    <div className="mt-3 inline-flex items-center text-xs uppercase tracking-[0.18em] text-[var(--tt-muted)]">
                      <span className="px-2 py-1 rounded-full bg-[var(--tt-surface-2)] text-[var(--tt-text)] font-semibold">
                        Self-score {a.self_score}/5
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {data.closing_reflections && Object.keys(data.closing_reflections).length > 0 && (
                <div className="mt-12">
                  <div className="text-xs uppercase tracking-[0.22em] text-[var(--tt-primary)] font-semibold mb-4">
                    Closing Reflections
                  </div>
                  <div className="space-y-6">
                    {Object.entries(data.closing_reflections).map(([q, ans], i) => (
                      <div key={i} className="border-l-2 border-[var(--tt-accent)] pl-5">
                        <p className="font-display text-base font-medium leading-snug">{q}</p>
                        <p className="mt-2 text-[var(--tt-text)] whitespace-pre-wrap leading-relaxed">{ans}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
